import React, { useState } from "react";

const MessageList = ({
    messages,
    onSelect,
    selectedMessageId,
    loading,
    currentPage,
    totalPages,
    totalCount,
    onLoadMore,
    loadingMore,
    onSearch,
    searchQuery,
    isSearching,
    displayMode = false,
}) => {
    const [hoveredId, setHoveredId] = useState(null);
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || "");

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (localSearchQuery.trim()) {
            onSearch(localSearchQuery.trim());
        }
    };

    const handleClearSearch = () => {
        setLocalSearchQuery("");
        onSearch("");
    };

    return (
        <div
            className={`flex flex-col h-full bg-background border-r border-border ${
                displayMode ? "p-6" : "p-5"
            }`}
            style={{
                flex: displayMode ? "0 0 36%" : "0 0 42%",
                minWidth: 0,
            }}
        >
            <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-border">
                <h3
                    className={`m-0 text-foreground font-bold tracking-tight ${
                        displayMode ? "text-[20px]" : "text-[15px]"
                    }`}
                >
                    Messages
                </h3>
                <div className="flex items-center gap-2">
                    <div
                        className={`text-primary-foreground bg-primary px-2.5 py-1 rounded-lg font-semibold ${
                            displayMode ? "text-[17px]" : "text-xs"
                        }`}
                    >
                        {messages.length}
                    </div>
                    {totalCount > 0 && (
                        <div
                            className={`text-muted-foreground font-medium ${
                                displayMode ? "text-[17px]" : "text-xs"
                            }`}
                        >
                            / {totalCount}
                        </div>
                    )}
                </div>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="relative">
                    <input
                        type="text"
                        value={localSearchQuery}
                        onChange={(e) => setLocalSearchQuery(e.target.value)}
                        placeholder="Search messages by id..."
                        className={`w-full px-4 py-2 pr-20 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                            displayMode ? "text-[17px]" : "text-sm"
                        }`}
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                        {localSearchQuery && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                title="Clear search"
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M18 6L6 18M6 6l12 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        )}
                        <button
                            type="submit"
                            className="p-1.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                            title="Search"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle
                                    cx="11"
                                    cy="11"
                                    r="8"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M21 21l-4.35-4.35"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
                {isSearching && (
                    <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
                        <span>
                            Searching for:{" "}
                            <strong className="text-foreground">
                                "{searchQuery}"
                            </strong>
                        </span>
                    </div>
                )}
            </form>

            {loading ? (
                <div className="flex items-center justify-center gap-3 p-10 text-muted-foreground">
                    <div className="w-5 h-5 border-3 border-border border-t-muted-foreground rounded-full animate-spin"></div>
                    <span className="font-medium">Loading messages...</span>
                </div>
            ) : (
                <div className="overflow-y-auto flex-1 pr-1 app-scrollbar pt-4">
                    {messages.length === 0 && (
                        <div className="card text-center p-10 border-2 border-dashed text-muted-foreground">
                            <div className="w-10 h-10 mx-auto mb-2">
                                {/* Mail / inbox icon */}
                                <svg
                                    width="40"
                                    height="40"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M3 8l9 6 9-6"
                                        className="stroke-border"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <rect
                                        x="2"
                                        y="4"
                                        width="20"
                                        height="16"
                                        rx="2"
                                        className="stroke-border"
                                        strokeWidth="1.5"
                                    />
                                </svg>
                            </div>
                            <div className="font-semibold">
                                {isSearching
                                    ? "No messages found"
                                    : "No messages in this collection"}
                            </div>
                        </div>
                    )}

                    {messages.map((m) => {
                        const isSelected = selectedMessageId === m._id;
                        const isHovered = hoveredId === m._id;

                        return (
                            <div
                                key={m._id}
                                onClick={() => onSelect(m)}
                                onMouseEnter={() => setHoveredId(m._id)}
                                onMouseLeave={() => setHoveredId(null)}
                                className={`card rounded-lg cursor-pointer mb-3 transition-all duration-200 ease-in-out relative overflow-hidden ${
                                    displayMode ? "p-5" : "p-4"
                                }
                  ${isSelected ? "border-2 border-primary shadow-lg scale-[1.01] -translate-y-0.5" : "border border-border"}
                  ${isHovered && !isSelected ? "shadow-md -translate-y-px" : "shadow-sm"}
                `}
                            >
                                {isSelected && (
                                    <div className="absolute top-0 left-0 w-1 h-full bg-primary"></div>
                                )}

                                <div className="flex justify-between mb-2 items-start">
                                    <div
                                        className={`font-bold text-foreground flex-1 font-mono ${
                                            displayMode
                                                ? "text-[18px]"
                                                : "text-sm"
                                        }`}
                                    >
                                        {m._id}
                                    </div>
                                    {m.level && (
                                        <div className="bg-muted text-muted-foreground px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border border-border ml-2">
                                            {m.level}
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`text-muted-foreground leading-relaxed mb-2 ${
                                        displayMode
                                            ? "text-[14px] line-clamp-2"
                                            : "text-[13px] line-clamp-2"
                                    }`}
                                >
                                    {m.message ||
                                        m.text ||
                                        m.from ||
                                        m.source ||
                                        m.hostname ||
                                        JSON.stringify(m).substring(0, 100)}
                                </div>

                                <div
                                    className={`flex justify-between items-center text-muted-foreground font-medium ${
                                        displayMode ? "text-[14px]" : "text-[11px]"
                                    }`}
                                >
                                    <div>{m.service && `${m.service}`}</div>
                                    <div>
                                        {m.timestamp || m.time
                                            ? new Date(
                                                  m.timestamp || m.time,
                                              ).toLocaleString()
                                            : ""}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Load More Button */}
                    {currentPage < totalPages && (
                        <div className="mt-3 mb-2">
                            <button
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 
                  ${
                      loadingMore
                          ? "bg-muted text-muted-foreground cursor-not-allowed"
                          : "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md active:scale-[0.98]"
                  }
                `}
                            >
                                {loadingMore ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
                                        <span>Loading...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center gap-2">
                                        <span>Load more messages</span>
                                    </div>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MessageList;
